import {
  CheckCircle as AcceptIcon,
  Cancel as DeclineIcon,
  Group as GroupIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import EmptyState from '../components/EmptyState'
import PageHeader from '../components/PageHeader'
import ToneChip from '../components/ToneChip'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders } from '../utils/api'

interface ConsentRequest {
  id: string
  group: {
    id: string
    name: string
    description?: string
  }
  invitedBy: {
    id: string
    name: string
    email: string
  }
  invitedAt: string
  email: string
}

const ConsentRequests = () => {
  const [requests, setRequests] = useState<ConsentRequest[]>([])
  const [loading, setLoading] = useState(true)
  // A failed load must not look like an empty list: `requests` stays [] either way.
  const [loadError, setLoadError] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState<ConsentRequest | null>(null)
  const [dialogType, setDialogType] = useState<'accept' | 'decline' | null>(null)
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const notify = useNotify()
  const translate = useTranslate()

  useEffect(() => {
    fetchConsentRequests()
  }, [])

  const fetchConsentRequests = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const response = await deadlineFetch('/api/group-members/my-consent-requests', {
        headers: authHeaders(),
      })

      if (response.ok) {
        const result = await response.json()
        setRequests(result.requests || [])
      } else {
        setLoadError(true)
        notify(translate('pos.consent.load_failed'), { type: 'error' })
      }
    } catch (error) {
      setLoadError(true)
      notify(translate('pos.consent.load_failed'), { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleConsentDecision = async (consent: 'granted' | 'denied') => {
    // Double-click / already-submitting guard (P71-7): without this, two
    // rapid clicks on Accept/Decline fired duplicate POSTs.
    if (!selectedRequest || submitting) return

    setSubmitting(true)
    try {
      const response = await deadlineFetch('/api/group-members/update-consent', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          membershipId: selectedRequest.id,
          consent,
          notes: notes.trim() || undefined,
        }),
      })

      if (response.ok) {
        const result = await response.json()
        notify(result.message, { type: 'success' })

        // Remove the request from the list
        setRequests((prev) => prev.filter((req) => req.id !== selectedRequest.id))

        // Close dialog
        setSelectedRequest(null)
        setDialogType(null)
        setNotes('')
      } else {
        const error = await response.json()
        notify(error.error || translate('pos.consent.update_failed'), { type: 'error' })
      }
    } catch (error) {
      notify(translate('pos.consent.update_failed'), { type: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  const openDialog = (request: ConsentRequest, type: 'accept' | 'decline') => {
    setSelectedRequest(request)
    setDialogType(type)
    setNotes('')
  }

  const closeDialog = () => {
    // Cancel during a submit used to close the dialog while the decision
    // still committed underneath it — block the close instead (P71-7).
    if (submitting) return
    setSelectedRequest(null)
    setDialogType(null)
    setNotes('')
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>{translate('pos.consent.loading')}</Typography>
        </CardContent>
      </Card>
    )
  }

  if (loadError) {
    return (
      <>
        <PageHeader title={translate('pos.consent.title')} />
        <Card>
          <CardContent>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => fetchConsentRequests()}>
                  {translate('pos.consent.retry')}
                </Button>
              }
            >
              {translate('pos.consent.load_failed')}
            </Alert>
          </CardContent>
        </Card>
      </>
    )
  }

  if (requests.length === 0) {
    return (
      <>
        <PageHeader title={translate('pos.consent.title')} />
        <Card>
          <EmptyState icon={<GroupIcon />} title={translate('pos.consent.no_pending')} />
        </Card>
      </>
    )
  }

  return (
    <>
      <PageHeader title={translate('pos.consent.title_with_count', { count: requests.length })} />
      <Card>
        <CardContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate('pos.consent.pending_warning')}
          </Alert>

          <List>
            {requests.map((request, index) => (
              <React.Fragment key={request.id}>
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {request.group.name}
                        </Typography>
                        <ToneChip tone="neutral" label={translate('pos.consent.pending_chip')} />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {request.group.description}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mt={1}>
                          <PersonIcon fontSize="small" />
                          <Typography variant="caption">
                            {translate('pos.consent.invited_by', {
                              name: request.invitedBy.name,
                              email: request.invitedBy.email,
                            })}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {translate('pos.consent.invited_on', {
                            date: new Date(request.invitedAt).toLocaleDateString(),
                          })}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<AcceptIcon />}
                        onClick={() => openDialog(request, 'accept')}
                      >
                        {translate('pos.consent.accept')}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeclineIcon />}
                        onClick={() => openDialog(request, 'decline')}
                      >
                        {translate('pos.consent.decline')}
                      </Button>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < requests.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Consent Decision Dialog */}
      <Dialog open={!!dialogType} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogType === 'accept'
            ? translate('pos.consent.accept_dialog_title')
            : translate('pos.consent.decline_dialog_title')}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Typography variant="body1" gutterBottom>
                {dialogType === 'accept'
                  ? translate('pos.consent.accept_question', { group: selectedRequest.group.name })
                  : translate('pos.consent.decline_question', {
                      group: selectedRequest.group.name,
                    })}
              </Typography>

              {dialogType === 'accept' && (
                <Alert severity="info" sx={{ my: 2 }}>
                  {translate('pos.consent.accept_warning')}
                </Alert>
              )}

              <TextField
                label={translate('pos.consent.notes_label')}
                multiline
                rows={3}
                fullWidth
                variant="outlined"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={
                  dialogType === 'accept'
                    ? translate('pos.consent.accept_notes_placeholder')
                    : translate('pos.consent.decline_notes_placeholder')
                }
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            {translate('pos.consent.cancel')}
          </Button>
          <Button
            onClick={() => handleConsentDecision(dialogType === 'accept' ? 'granted' : 'denied')}
            variant="contained"
            color={dialogType === 'accept' ? 'success' : 'error'}
            disabled={submitting}
          >
            {dialogType === 'accept'
              ? translate('pos.consent.accept')
              : translate('pos.consent.decline')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ConsentRequests
